import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { Document, ReplaceRequestResponse } from '../../../js/api';
import { DocumentsService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { parseFormDate } from '../utils/date';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './dialog';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Field, Fieldset, Label } from './fieldset';
import 'react-datepicker/dist/react-datepicker.css';

type DocumentReplaceDialogProps = {
  open: boolean;
  doc: Document | null;
  onClose: () => void;
  onSuccess: () => void;
  isExpirable: boolean;
};

const replaceFormSchema = z.object({
  file: z.instanceof(File, { message: 'A file is required' }),
  issued_on: z.date().nullable().optional(),
  expires_on: z.date().nullable().optional(),
  // Signature-requiring types only: checked restarts e-sign on the replacement,
  // unchecked records an already-signed replacement.
  request_esignature: z.boolean().optional(),
});

type ReplaceFormData = z.infer<typeof replaceFormSchema>;

export function DocumentReplaceDialog({
  open,
  doc,
  onClose,
  onSuccess,
  isExpirable,
}: DocumentReplaceDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<ReplaceFormData>({
    resolver: zodResolver(replaceFormSchema),
    mode: 'onChange',
    defaultValues: {
      issued_on: null,
      expires_on: null,
      request_esignature: true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({ issued_on: null, expires_on: null, request_esignature: true });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open, form]);

  const acceptAttr = useMemo(() => {
    const mimeTypes = doc?.document_type?.allowed_mime_types;
    if (!mimeTypes || mimeTypes.length === 0) return undefined;
    return mimeTypes.join(',');
  }, [doc]);

  const resetForm = () => {
    form.reset({ issued_on: null, expires_on: null, request_esignature: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const replaceMutation = useMutation({
    mutationFn: async (data: ReplaceFormData) => {
      if (!doc) throw new Error('No document selected');

      const requestBody = {
        original_filename: data.file.name,
        file_size: data.file.size,
        mime_type: data.file.type,
        issued_on: data.issued_on ? parseFormDate(data.issued_on) : undefined,
        expires_on: data.expires_on ? parseFormDate(data.expires_on) : undefined,
        // Ignored on the replace-request step; honored on confirm-replace for
        // signature-requiring types.
        request_esignature: data.request_esignature ?? true,
      };

      // Step 1: Request a presigned replace URL
      const replaceResponse: ReplaceRequestResponse = await DocumentsService.documentsReplaceCreate(
        {
          id: doc.id,
          requestBody,
        }
      );

      // Step 2: PUT file to S3 with the presigned URL
      const putResponse = await fetch(replaceResponse.upload_url, {
        method: 'PUT',
        body: data.file,
        headers: {
          'Content-Type': data.file.type,
        },
      });

      if (!putResponse.ok) {
        throw new Error(`S3 upload failed with status ${putResponse.status}`);
      }

      // Step 3: Confirm the replacement
      return DocumentsService.documentsConfirmReplaceCreate({
        id: doc.id,
        requestBody,
      });
    },
    onSuccess: () => {
      toast.success('Document replaced successfully');
      resetForm();
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Replace failed' });
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    if (isExpirable) {
      let hasError = false;
      if (!data.issued_on) {
        form.setError('issued_on', { message: 'Issue date is required' });
        hasError = true;
      }
      if (!data.expires_on) {
        form.setError('expires_on', { message: 'Expiration date is required' });
        hasError = true;
      }
      if (data.issued_on && data.expires_on && data.expires_on <= data.issued_on) {
        form.setError('expires_on', { message: 'Expiration date must be after the issue date' });
        hasError = true;
      }
      if (hasError) return;
    }
    replaceMutation.mutate(data);
  });

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        Replace Document
      </DialogTitle>
      <DialogBody>
        <form onSubmit={onSubmit}>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              {doc && (
                <p className="tw:text-sm tw:text-zinc-600">
                  Replacing:{' '}
                  <span className="tw:font-medium">{doc.label || doc.original_filename}</span>
                </p>
              )}

              {isExpirable && (
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                  <Field>
                    <Label>
                      Issue Date <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <Controller
                      name="issued_on"
                      control={form.control}
                      render={({ field }) => (
                        <ReactDatePicker
                          className="datepicker-input"
                          wrapperClassName="datepicker-wrapper"
                          selected={field.value ?? null}
                          onChange={(date) => field.onChange(date)}
                          placeholderText="Select issue date"
                          disabled={replaceMutation.isPending}
                          dateFormat="MM/dd/yyyy"
                        />
                      )}
                    />
                    {form.formState.errors.issued_on && (
                      <FieldValidationErrorMessage
                        message={form.formState.errors.issued_on.message}
                      />
                    )}
                  </Field>
                  <Field>
                    <Label>
                      Expiration Date <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <Controller
                      name="expires_on"
                      control={form.control}
                      render={({ field }) => (
                        <ReactDatePicker
                          className="datepicker-input"
                          wrapperClassName="datepicker-wrapper"
                          selected={field.value ?? null}
                          onChange={(date) => field.onChange(date)}
                          placeholderText="Select expiration date"
                          disabled={replaceMutation.isPending}
                          dateFormat="MM/dd/yyyy"
                        />
                      )}
                    />
                    {form.formState.errors.expires_on && (
                      <FieldValidationErrorMessage
                        message={form.formState.errors.expires_on.message}
                      />
                    )}
                  </Field>
                </div>
              )}

              <Field>
                <Label>
                  File <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptAttr}
                  disabled={replaceMutation.isPending}
                  onChange={(e) => {
                    const next = e.target.files?.[0];
                    if (next) {
                      form.setValue('file', next, { shouldValidate: true });
                    } else {
                      form.setValue('file', undefined as unknown as File, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  className="tw:block tw:w-full tw:text-sm tw:text-zinc-700 tw:file:mr-3 tw:file:rounded-md tw:file:border-0 tw:file:bg-brand-green-2 tw:file:px-3 tw:file:py-1.5 tw:file:text-sm tw:file:font-medium tw:file:text-brand-green-0 tw:hover:file:bg-brand-green-1 tw:hover:file:text-white tw:disabled:opacity-50 tw:disabled:cursor-not-allowed"
                />
                {form.formState.errors.file && (
                  <FieldValidationErrorMessage message={form.formState.errors.file.message} />
                )}
                {doc?.document_type?.max_upload_size_mb && (
                  <p className="tw:!mt-1 tw:text-xs tw:text-zinc-500">
                    Maximum file size: {doc.document_type.max_upload_size_mb} MB
                  </p>
                )}
              </Field>
              {doc?.document_type?.require_signature && (
                <Field>
                  <Controller
                    name="request_esignature"
                    control={form.control}
                    render={({ field }) => (
                      <CheckboxField>
                        <Checkbox
                          color="brandgreen1"
                          checked={field.value ?? true}
                          onChange={field.onChange}
                          disabled={replaceMutation.isPending}
                          data-testid="replace-request-esignature-checkbox"
                        />
                        <Label>Request E-Signature</Label>
                      </CheckboxField>
                    )}
                  />
                  <p className="tw:!mt-1 tw:text-xs tw:text-zinc-500">
                    Replacing restarts the signature process. Leave checked to e-sign the new file,
                    or uncheck if it has already been signed off-platform.
                  </p>
                </Field>
              )}
            </div>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose} disabled={replaceMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="brandgreen0"
          onClick={onSubmit}
          disabled={!form.formState.isValid || replaceMutation.isPending}
          data-testid="document-replace-submit"
        >
          {replaceMutation.isPending ? 'Replacing...' : 'Replace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
